
import React from "react";
import { ArrowRight, Clock, FileText, CheckSquare } from "lucide-react";
import { Link } from "react-router-dom";

interface QuizStartProps {
  onStart: () => void;
}

const QuizStart: React.FC<QuizStartProps> = ({ onStart }) => {
  return (
    <section className="section">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="gradient-bg text-white p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">Board Assessment</h2>
              <p className="text-lg max-w-2xl mx-auto">
                Evaluate your board's preparedness for AI oversight and receive a personalized report with actionable recommendations.
              </p>
            </div>
            
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-techne-purple flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900">5-Minute Assessment</h3>
                    <p className="text-sm text-gray-600">Quick questions designed for busy executives</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckSquare className="w-6 h-6 text-techne-purple flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900">10 Key Questions</h3>
                    <p className="text-sm text-gray-600">Covering essential AI governance areas</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="w-6 h-6 text-techne-purple flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900">Personalized Report</h3>
                    <p className="text-sm text-gray-600">Get actionable insights and recommendations</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-xl font-semibold mb-4">What You'll Learn:</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-techne-primaryPurple/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-techne-primaryPurple text-xs font-medium">1</span>
                    </div>
                    <span className="text-gray-700">Your board's AI governance maturity score</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-techne-primaryPurple/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-techne-primaryPurple text-xs font-medium">2</span>
                    </div>
                    <span className="text-gray-700">Key strengths in your current approach</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-techne-primaryPurple/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-techne-primaryPurple text-xs font-medium">3</span>
                    </div>
                    <span className="text-gray-700">Critical gaps that need immediate attention</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-techne-primaryPurple/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-techne-primaryPurple text-xs font-medium">4</span>
                    </div>
                    <span className="text-gray-700">Actionable next steps for improving AI governance</span>
                  </li>
                </ul>
                
                <div className="text-center">
                  <button 
                    onClick={onStart}
                    className="btn-primary text-lg px-8 py-3 inline-flex items-center gap-2"
                  >
                    Begin Assessment
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    Your data is secure and will only be used to generate your personalized report.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuizStart;
